import { createServerFn } from '@tanstack/react-start'
import { z } from 'zod'
import { requireSupabaseAuth } from '@/integrations/supabase/auth-middleware'
import { requireManager } from './role-guard'

type Ctx = { supabase: { from: (t: string) => any }; userId: string }

async function assertAdmin(context: unknown) {
  const { supabase, userId } = await requireManager(context)
  return { supabase, userId }
}

const addSchema = z.object({
  booking_id: z.string().uuid(),
  label: z.string().trim().min(1).max(120),
  amount_cents: z.number().int().min(-1_000_000).max(1_000_000),
  note: z.string().trim().max(500).nullable().optional(),
  charge_date: z
    .string()
    .regex(/^\d{4}-\d{2}-\d{2}$/)
    .nullable()
    .optional(),
})

/** Staff add fuel, damage or other miscellaneous charges to a guest's stay. */
export const addBookingCharge = createServerFn({ method: 'POST' })
  .middleware([requireSupabaseAuth])
  .inputValidator((data: unknown) => addSchema.parse(data))
  .handler(async ({ data, context }) => {
    const { supabase, userId } = await assertAdmin(context)
    const { data: row, error } = await supabase
      .from('booking_charges')
      .insert({
        booking_id: data.booking_id,
        label: data.label,
        amount_cents: data.amount_cents,
        note: data.note ?? null,
        charge_date: data.charge_date ?? null,
        created_by: userId,
      })
      .select('id,booking_id,label,amount_cents,note,charge_date,created_at')
      .maybeSingle()
    if (error) throw new Error(error.message)
    return { ok: true, charge: row }
  })

export const deleteBookingCharge = createServerFn({ method: 'POST' })
  .middleware([requireSupabaseAuth])
  .inputValidator((data: unknown) => z.object({ id: z.string().uuid() }).parse(data))
  .handler(async ({ data, context }) => {
    const { supabase } = await assertAdmin(context)
    const { error } = await supabase.from('booking_charges').delete().eq('id', data.id)
    if (error) throw new Error(error.message)
    return { ok: true }
  })
